import { Skeleton } from "@mui/material";

import { useNavigation } from "@/providers";
import {
  RobotoStyledPopoverMenu as Menu,
  RobotoMenuItem as MenuItem,
  RobotoLinkButton as LinkButton,
  RobotoLink as Link,
} from "@/shared/components";
import { FileRecord } from "@/shared/domain/files";
import { basename, dirname } from "@/shared/domain/files/filesystem";
import { usePopupState } from "@/shared/hooks";
import { Goto } from "@/shared/navigation";

import styles from "./FileInfo.module.css";

/**
 * Displays file information - either a single filename or count of multiple files.
 */
export function FileInfo({
  isFilesLoading,
  files,
}: {
  isFilesLoading: boolean;
  files: FileRecord[];
}) {
  const { goto } = useNavigation();
  const popup = usePopupState();

  const fileNames = files
    .map((file) => basename(file?.relative_path ?? ""))
    .join(", ");

  if (isFilesLoading) {
    return <Skeleton animation="wave" variant="text" width="160px" />;
  }

  if (files.length === 1) {
    const file = files[0];
    return (
      <div className={styles.fileInfo}>
        <Link
          title={fileNames}
          to={Goto.createAssociationRoute(
            file.association_id,
            dirname(file.relative_path),
          )}
        >
          {basename(file.relative_path)}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Menu anchorEl={popup.anchor} open={popup.isOpen} onClose={popup.close}>
        {files.map((file) => {
          return (
            <MenuItem
              key={file.file_id}
              eventName={"ViewDatasetForFileClicked"}
              eventProperties={{
                datasetId: file.association_id,
                fileId: file.file_id,
              }}
              onClick={() =>
                goto.association({
                  associationId: file.association_id,
                  directoryPath: dirname(file.relative_path),
                })
              }
            >
              {basename(file.relative_path)}
            </MenuItem>
          );
        })}
      </Menu>

      <div className={styles.fileInfo}>
        <LinkButton
          component="span"
          underline="hover"
          onClick={(event) => popup.open({ anchorEl: event.currentTarget })}
          role="button"
          title={fileNames}
        >
          {`${files.length} Files`}
        </LinkButton>
      </div>
    </>
  );
}
